import { LoadingCircle, TextButton } from "@industriousoffice/react-components";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { formatDateFromYYYYMMDD } from "@/utils/date";
import { Invoice, PaymentStatus } from "@/api/opb/opbTypes";
import PaymentStatusTag from "@/components/PaymentStatusTag";

import { formatCurrency } from "../../utils/string";

interface InvoiceCardProps {
  invoices: Invoice[];
  isLoading: boolean;
  currencyCode: string;
  footer?: ReactNode;
  withAction?: boolean;
  onAction?: (invoiceId: string) => void;
  actionText?: string;
  emptyStateText?: string;
}

function InvoiceCard({
  invoices,
  isLoading,
  currencyCode,
  footer,
  withAction,
  onAction,
  actionText,
  emptyStateText,
}: InvoiceCardProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation(["common", "dashboard"]);

  if (isLoading) {
    return (
      <div className="items-center justify-between overflow-x-auto border rounded-lg border-grey-60">
        <div>
          <div className="w-full text-center">
            <div className="flex items-center justify-center py-5">
              <LoadingCircle spinnerColor="#013e3f" spinnerSize={30} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="items-center justify-between overflow-x-auto border rounded-lg border-grey-60">
      {invoices.length === 0 && !isLoading ? (
        <div className="pt-4 pb-4 pl-4 text-sm font-normal text-left text-grey-30 font-hanken">
          {emptyStateText ?? t("invoiceEmptyState")}
        </div>
      ) : (
        <>
          {invoices.map((invoice) => (
            <div
              key={`${invoice.id}-card-row`}
              className="flex flex-row px-4 pt-4 border-b"
            >
              <div className="w-full text-sm font-normal text-left font-hanken text-grey-30">
                {invoice.invoiceNumber}
                <div className="flex-row pt-2 pb-4">
                  <PaymentStatusTag
                    invoice={invoice}
                    className="inline-block text-xs text-center font-hanken text-ellipsis"
                  />
                  <span className="pl-2">
                    {formatDateFromYYYYMMDD(invoice.dueDate, "PP")}
                  </span>
                </div>
              </div>
              <div className="items-end text-base font-bold text-end font-hanken">
                {formatCurrency({
                  value:
                    invoice.paymentStatus !== PaymentStatus.Paid
                      ? invoice.balance
                      : invoice.amount,
                  currency: currencyCode,
                  lang: language,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                {withAction ? (
                  <TextButton
                    type="button"
                    onClick={() => onAction?.(invoice.id)}
                    className="font-normal"
                  >
                    {actionText}
                  </TextButton>
                ) : null}
              </div>
            </div>
          ))}
        </>
      )}
      {!isLoading ? footer : null}
    </div>
  );
}

export default InvoiceCard;
